import React, { FunctionComponent, useContext, useState } from 'react'
import { useFeathersClient } from '@strac/react-client-feathers'
import { useQuery } from '@tanstack/react-query'
import { arrayToMap, getAllData } from '../../../../utils/data'
import useQueryListener from '../../../../hooks/data/useQueryListener'
import { App, Button, List, Tooltip, Typography } from 'antd'
import {
  AimOutlined,
  AlertOutlined,
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
  SaveOutlined,
  TagOutlined
} from '@ant-design/icons'
import { VehicleLocation, GeofenceDevice, Geofence, GeofenceActivity } from 'server'
import useDefaultVehicleFilter from '../../../../hooks/data/useDefaultVehicleFilter'
import { useLoaderData, useMatch } from '@tanstack/react-router'
import CollectionModal from '../../../Collections/CollectionsList/CollectionModal/CollectionModal'
import DeviceSelectionModal from '../../../../components/DeviceSelectionModal/DeviceSelectionModal'
import { AuthContext } from '@strac/react-client-auth'
import { BatchCall } from 'feathers-batch'
import { green, red } from '@ant-design/colors'
import { useMap } from '@vis.gl/react-google-maps'
import { fromNow } from '../../../../utils/time'

interface IProps {
  geofence: Geofence
}

const GeofenceListItem: FunctionComponent<any> = (props: IProps) => {
  const { geofence } = props
  const { authUser } = useContext(AuthContext) as any

  const match = useMatch({ strict: false })
  const { vehicles, vehicleLocations } = useLoaderData({ from: match.routeId ?? '/' })

  const client = useFeathersClient()
  const { notification } = App.useApp()

  const map = useMap()

  const [editGeofenceDevicesId, setEditGeofenceDevicesId] = useState<number | undefined>(undefined)
  const [collectionModalOpen, setCollectionModalOpen] = useState(false)
  const [initTargetKeys, setInitTargetKeys] = useState<number[]>([])
  const [targetKeys, setTargetKeys] = useState<number[]>([])

  const [updateGeofenceLoading, setUpdateGeofenceLoading] = useState<boolean>(false)

  const geofenceDevicesQuery = {
    geofence_id: geofence.id,
    active: true
  }
  const {
    data: geofenceDevices,
    isLoading: geofenceDevicesLoading,
    refetch: refetchGeofenceDevices
  } = useQuery({
    queryKey: ['geofence-device', 'find', geofenceDevicesQuery],
    queryFn: () => getAllData(client, 'geofence-device', geofenceDevicesQuery),
    placeholderData: [],
    gcTime: 0
  })

  useQueryListener('geofence-device', refetchGeofenceDevices)

  const geofenceDeviceIds = geofenceDevices.map((geofenceDevice: GeofenceDevice) => geofenceDevice.id)

  const geofenceActivityQuery = {
    geofence_device_id: { $in: geofenceDeviceIds },
    $sort: {
      id: -1
    }
  }
  const { data: geofenceActivity, refetch: refetchGeofenceActivity } = useQuery({
    queryKey: ['geofence-activity', 'find', geofenceActivityQuery],
    queryFn: () => getAllData(client, 'geofence-activity', geofenceActivityQuery),
    placeholderData: [],
    enabled: geofenceDeviceIds.length > 0
  })

  useQueryListener('geofence-activity', refetchGeofenceActivity)

  const position = new google.maps.LatLng(geofence.center_latitude, geofence.center_longitude)
  const radius = geofence.radius

  const filteredGeofenceDevices = geofenceDevices.filter(
    (geofenceDevice: GeofenceDevice) => geofenceDevice.geofence_id === geofence.id
  )
  const geofenceDevicesIds = filteredGeofenceDevices.map(
    (geofenceDevice: GeofenceDevice) => geofenceDevice.vehicle_id
  )

  const [removeGeofenceLoading, setRemoveGeofenceLoading] = useState<boolean>(false)
  const [addRemoveGeofenceDeviceLoading, setAddRemoveGeofenceDeviceLoading] = useState<number | undefined>(
    undefined
  )

  const filteredVehicleLocations = useDefaultVehicleFilter(vehicles, vehicleLocations)

  const vehicleLocationsInCircle = getVehiclesInGeofence(filteredVehicleLocations, position, radius)
  const vehicleLocationsInCircleIds = vehicleLocationsInCircle.map(
    (vehicleLocation: any) => vehicleLocation.vehicle_id
  )

  // Merge for unique vehicles
  const uniqueVehicleIds = Array.from(new Set([...geofenceDevicesIds, ...vehicleLocationsInCircleIds]))

  const allVehiclesQuery = {
    vehicle_id: { $in: uniqueVehicleIds }
  }
  const {
    data: allVehicles,
    isLoading: allVehiclesLoading,
    refetch: refetchVehicles
  } = useQuery({
    queryKey: ['vehicle', 'find', allVehiclesQuery],
    queryFn: () => getAllData(client, 'vehicle', allVehiclesQuery),
    placeholderData: [],
    enabled: uniqueVehicleIds.length > 0
  })

  useQueryListener('vehicle', refetchVehicles)

  const {
    data: allVehicleLocations,
    isLoading: allVehicleLocationsLoading,
    refetch: refetchVehicleLocations
  } = useQuery({
    queryKey: ['vehicle-location', 'find', allVehiclesQuery],
    queryFn: () => getAllData(client, 'vehicle-location', allVehiclesQuery),
    placeholderData: [],
    enabled: uniqueVehicleIds.length > 0
  })

  useQueryListener('vehicle-location', refetchVehicleLocations)

  const vehicleMap = arrayToMap(allVehicles, 'vehicle_id')

  const getGeofenceDevice = (vehicleId: number) => {
    return geofenceDevices.find(
      (geofenceDevice: GeofenceDevice) =>
        geofenceDevice.vehicle_id === vehicleId && geofence.id === geofenceDevice.geofence_id
    )
  }

  const addVehicleToGeofence = (vehicleId: number) => {
    setAddRemoveGeofenceDeviceLoading(vehicleId)
    client
      .service('geofence-device')
      .create({
        geofence_id: geofence.id,
        vehicle_id: vehicleId
      })
      .then((response: GeofenceDevice) => {
        const vehicle = vehicleMap.get(vehicleId)
        notification.success({
          message: 'Vehicle Added',
          description: `Vehicle '${vehicle.name}' has been added to geofence '${geofence.name}'`
        })
      })
      .catch((error: any) => {
        notification.error({
          message: 'Error adding vehicle',
          description: error.message
        })
      })
      .finally(() => {
        setAddRemoveGeofenceDeviceLoading(undefined)
      })
  }

  const removeVehicleFromGeofence = (vehicleId: number) => {
    const geofenceDevice = getGeofenceDevice(vehicleId)
    if (geofenceDevice) {
      setAddRemoveGeofenceDeviceLoading(vehicleId)
      const vehicle = vehicleMap.get(vehicleId)
      client
        .service('geofence-device')
        .patch(geofenceDevice.id, {
          active: false
        })
        .then((response: GeofenceDevice) => {
          notification.success({
            message: 'Vehicle Removed',
            description: `Vehicle '${vehicle.name}' has been removed from geofence '${geofence.name}'`
          })
        })
        .catch((error: any) => {
          notification.error({
            message: 'Error removing vehicle',
            description: error.message
          })
        })
        .finally(() => {
          setAddRemoveGeofenceDeviceLoading(undefined)
        })
    }
  }

  const updateGeofence = (name: string) => {
    setUpdateGeofenceLoading(true)
    client
      .service('geofence')
      .patch(geofence.id, {
        name: name
      })
      .then((response: Geofence) => {
        notification.success({
          message: 'Geofence Updated',
          description: `Geofence '${response.name}' has been updated`
        })
      })
      .catch((error: any) => {
        notification.error({
          message: 'Error updating geofence',
          description: error.message
        })
      })
      .finally(() => setUpdateGeofenceLoading(false))
  }

  const removeGeofence = (geofenceId: number) => {
    setRemoveGeofenceLoading(true)
    client
      .service('geofence')
      .patch(geofenceId, { active: false })
      .then((response: Geofence) => {
        notification.success({
          message: 'Geofence Removed',
          description: `Geofence '${response.name}' has been removed`
        })
      })
      .catch((error: any) => {
        console.error(error)
        notification.error({
          message: 'Error removing geofence',
          description: error.message
        })
      })
      .finally(() => {
        setRemoveGeofenceLoading(false)
      })
  }

  const handleCreateCollection = (geofence: Geofence) => {
    const position = new google.maps.LatLng(geofence.center_latitude, geofence.center_longitude)
    const radius = geofence.radius

    const vehicleLocationsInCircle = getVehiclesInGeofence(filteredVehicleLocations, position, radius)
    const vehicleIds = vehicleLocationsInCircle.map((vehicleLocation: any) => vehicleLocation.vehicle_id)
    setInitTargetKeys(vehicleIds)
    setCollectionModalOpen(true)
  }

  const handleEditSelectedDevices = (geofence: Geofence) => {
    const vehicleIds = geofenceDevices
      .filter((geofenceDevice: GeofenceDevice) => geofenceDevice.geofence_id === geofence.id)
      .map((geofenceDevice: GeofenceDevice) => geofenceDevice.vehicle_id)
    setInitTargetKeys(vehicleIds)
    setEditGeofenceDevicesId(geofence.id)
  }

  const renderVehicle = (vehicleLocation: VehicleLocation) => {
    const vehicle = vehicleMap.get(vehicleLocation.vehicle_id)
    const saved = geofenceDevicesIds.includes(vehicleLocation.vehicle_id)
    const inGeofence = vehicleLocationsInCircleIds.includes(vehicleLocation.vehicle_id)

    const geofenceDevice = geofenceDevices.find(
      (gD: GeofenceDevice) => gD.vehicle_id === vehicleLocation.vehicle_id && gD.geofence_id === geofence.id
    )

    const lastGeofenceActivity: GeofenceActivity | undefined = geofenceActivity?.filter(
      (activity: GeofenceActivity) => activity.geofence_device_id === geofenceDevice?.id
    )[0]

    // const formattedTimestamp = formatTimestamp(lastGeofenceActivity?.created_at, 'M/D/YY h:mm:ss')
    const formattedTimestamp = fromNow(lastGeofenceActivity?.created_at)

    const current = inGeofence ? 'In Geofence' : 'Outside Geofence'
    const last = lastGeofenceActivity?.type === 1 ? 'Entered' : 'Exited'
    const timestampText = lastGeofenceActivity ? `${last} ${formattedTimestamp}` : ''

    return (
      <List.Item
        actions={[
          <Tooltip title={saved ? 'Saved' : 'Unsaved'}>
            <SaveOutlined key={'save-icon'} style={{ color: saved ? green[5] : red[5] }} />
          </Tooltip>,
          <Button
            key={'add-remove-geofence'}
            loading={addRemoveGeofenceDeviceLoading === vehicleLocation.vehicle_id}
            type={'primary'}
            danger={saved}
            ghost={true}
            icon={saved ? <MinusOutlined /> : <PlusOutlined />}
            onClick={
              saved
                ? () => removeVehicleFromGeofence(vehicleLocation.vehicle_id)
                : () => addVehicleToGeofence(vehicleLocation.vehicle_id)
            }
          />
        ]}
      >
        <List.Item.Meta
          title={<Typography.Text ellipsis={true}>{vehicle?.name}</Typography.Text>}
          description={`${current}${timestampText ? ` - ${timestampText}` : ''}`}
        />
      </List.Item>
    )
  }

  const handleDone = async () => {
    const filteredGeofenceDevices = geofenceDevices.filter(
      (geofenceDevice: GeofenceDevice) => geofenceDevice.geofence_id === editGeofenceDevicesId
    )
    const geofenceDeviceIds = filteredGeofenceDevices.map(
      (geofenceDevice: GeofenceDevice) => geofenceDevice.vehicle_id
    )

    const idsToRemove = geofenceDeviceIds.filter(
      (geofenceDeviceId: number) => !targetKeys?.includes(geofenceDeviceId)
    )
    const idsToAdd: number[] = (targetKeys || []).filter(
      (vehicleId: number) => !geofenceDeviceIds.includes(vehicleId)
    )

    const calls: BatchCall[] = []

    idsToRemove.forEach((vehicleId: number) => {
      const geofenceDevice = geofenceDevices.find(
        (geofenceDevice: GeofenceDevice) => geofenceDevice.vehicle_id === vehicleId
      )
      if (geofenceDevice) {
        calls.push(['patch', 'geofence-device', geofenceDevice.id, { active: false }])
      }
    })

    idsToAdd.forEach((vehicleId: number) => {
      calls.push(['create', 'geofence-device', { geofence_id: editGeofenceDevicesId, vehicle_id: vehicleId }])
    })

    await client
      .service('batch')
      .create({ calls })
      .then((response: any) => {
        setEditGeofenceDevicesId(undefined)
      })
  }

  function focusGeofence(
    map: google.maps.Map | null | undefined,
    center: google.maps.LatLng,
    radiusMeters: number
  ) {
    if (!map) {
      return
    }
    // Create a dummy circle to compute bounds
    const circle = new google.maps.Circle({
      center,
      radius: radiusMeters
    })

    const bounds = circle.getBounds()
    if (bounds) {
      map.fitBounds(bounds, {
        top: 300,
        bottom: 300,
        left: 300,
        right: 300
      }) // Optional padding
    } else {
      map.setCenter(center)
      map.setZoom(15) // Fallback zoom level
    }
  }

  return (
    <>
      <CollectionModal
        userId={authUser.id}
        open={collectionModalOpen}
        setOpen={setCollectionModalOpen}
        initTargetKeys={initTargetKeys}
      />

      <DeviceSelectionModal
        open={!!editGeofenceDevicesId}
        setOpen={setEditGeofenceDevicesId}
        initSelectedKeys={initTargetKeys}
        targetKeys={targetKeys}
        setTargetKeys={setTargetKeys}
        handleDone={handleDone}
      />

      <List.Item
        actions={[
          <Tooltip title={'Pan To'}>
            <Button
              disabled={!map}
              key={'pan-to'}
              onClick={() => {
                const latLng = new google.maps.LatLng(geofence.center_latitude, geofence.center_longitude)
                focusGeofence(map, latLng, geofence.radius)
              }}
              type={'primary'}
              ghost={true}
              icon={<AimOutlined />}
            />
          </Tooltip>,
          <Tooltip title={'Create Collection'}>
            <Button
              key={'create-collection'}
              onClick={() => handleCreateCollection(geofence)}
              type={'primary'}
              icon={<TagOutlined />}
            />
          </Tooltip>,
          <Tooltip title={'Edit Devices for Alerts'}>
            <Button
              key={'edit-selected-devices'}
              type={'primary'}
              onClick={() => handleEditSelectedDevices(geofence)}
              icon={<AlertOutlined />}
            />
          </Tooltip>,
          <Tooltip title={'Remove Geofence'}>
            <Button
              loading={removeGeofenceLoading}
              key={'delete'}
              danger={true}
              onClick={() => removeGeofence(geofence.id)}
              icon={<DeleteOutlined />}
            />
          </Tooltip>
        ]}
      >
        <List.Item.Meta
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text
                style={{
                  margin: '12px'
                }}
                editable={{
                  onChange: (value: string) => {
                    updateGeofence(value)
                  },
                  icon: updateGeofenceLoading ? <LoadingOutlined /> : <EditOutlined />
                }}
              >
                {geofence.name}
              </Typography.Text>
            </div>
          }
        />
      </List.Item>
      <List.Item>
        <List
          loading={allVehicleLocationsLoading || allVehiclesLoading || geofenceDevicesLoading}
          style={{ width: '100%' }}
          renderItem={renderVehicle}
          dataSource={allVehicleLocations}
          size={'small'}
        />
      </List.Item>
    </>
  )
}

export default GeofenceListItem

export const getVehiclesInGeofence = (
  vehicleLocations: VehicleLocation[],
  position: google.maps.LatLng | null,
  radius: number
): VehicleLocation[] => {
  return vehicleLocations.filter((vehicleLocation: VehicleLocation) => {
    if (!position || !radius || !vehicleLocation) return false
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(vehicleLocation.latitude, vehicleLocation.longitude),
      position
    )
    // console.log(`distance ${distance} <= circleRadius ${circleRadius}`)
    return distance <= radius
  })
}
